import { useCallback, useMemo, useRef, useState } from "react";
import { darken, lighten, Stack, useTheme } from "@mui/material";
import { Button, Chip, IUppyUploader, UppyUploader, Typography } from "@powerdiary/components-library/lib";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import type Uppy from "@uppy/core";
import { DocumentUploadConfigModel } from "types/generated/models";
import { useClientPortalConfigContext } from "../../../../context";
import { useRefreshDocuments } from "../../hooks/useRefreshDocuments";
import { getUploadResponseError } from "../../../../utils/uploadError";

interface SelectedFile {
    id: string;
    name: string;
}

function useUppy() {
    const uppyRef = useRef<Uppy | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);

    const passUppyBack = useCallback((uppy: Uppy) => {
        uppyRef.current = uppy;

        const syncFileState = () => {
            const files = uppy.getState().files ?? {};
            setSelectedFiles(Object.entries(files).map(([id, file]) => ({ id, name: file.name })));
        };

        uppy.on("file-added", syncFileState);
        uppy.on("file-removed", syncFileState);
        uppy.on("complete", syncFileState);
    }, []);

    const upload = () => void uppyRef.current?.upload();
    const removeFile = (fileId: string) => uppyRef.current?.removeFile(fileId);

    return { passUppyBack, selectedFiles, upload, removeFile };
}

export default function DocumentUpload({
    config,
}: Readonly<{
    config: DocumentUploadConfigModel;
}>) {
    const theme = useTheme();
    const { clientPortalGuid } = useClientPortalConfigContext();
    const refreshDocuments = useRefreshDocuments(clientPortalGuid);
    const { passUppyBack, selectedFiles, upload, removeFile } = useUppy();

    const uploadEndpoint = `/api/v1/${clientPortalGuid}/Documents/Upload`;

    const uppyProps = useMemo(
        () =>
            ({
                endpoint: uploadEndpoint,
                height: 200,
                maxFileSize: config.maxFileSize,
                maxNumberOfFiles: config.maxFileCount,
                allowedFileTypes: config.allowedExtensions,
                autoProceed: false,
                showSelectedFiles: false,
                hideUploadButton: true,
                hideProgressAfterFinish: true,
                disableInformer: true,
                showMessage: (message: string) => enqueueSnackbar(message, { variant: "error" }),
                passUppyBack,
                onFileUploaded: refreshDocuments,
                getResponseError: getUploadResponseError,
                color: {
                    main: theme.palette.primary.main,
                    light: lighten(theme.palette.primary.main, 0.9),
                    dark: darken(theme.palette.primary.main, 0.3),
                },
            }) as IUppyUploader,
        [
            uploadEndpoint,
            config.maxFileSize,
            config.maxFileCount,
            config.allowedExtensions,
            theme.palette.primary.main,
            passUppyBack,
            refreshDocuments,
        ]
    );

    return (
        <Stack spacing={2} data-test-id="section_document_upload">
            <Typography variant="heading4" data-test-id="text_upload_documents_heading">
                Upload Documents
            </Typography>
            <UppyUploader {...uppyProps} />
            {selectedFiles.length > 0 && (
                <Stack flexDirection="row" flexWrap="wrap" gap={1} data-test-id="section_selected_files">
                    {selectedFiles.map((file) => (
                        <Chip
                            key={file.id}
                            label={file.name}
                            onDelete={() => removeFile(file.id)}
                            data-test-id={`chip_file_${file.id}`}
                        />
                    ))}
                </Stack>
            )}
            <Button
                variant="contained"
                onClick={upload}
                disabled={selectedFiles.length === 0}
                data-test-id="button_upload"
                sx={{ alignSelf: "flex-end" }}
            >
                Upload
            </Button>
        </Stack>
    );
}
